"use client";
import { useState, useEffect, useRef } from "react";
import { cookieName, cookieIsChangeLang } from "@/app/i18n/settings";
import { setCookie } from "cookies-next";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useTranslation } from "@/app/i18n/client";
import { ChevronDown, Check, Search, MenuIcon } from "lucide-react";
import Link from "next/link";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";
import { ShoppingCatagory } from "./nav-ulife-server-component";
import { CUSTOMER_LEVEL } from "@prisma/client";
import { WebNavigationComponent } from "@/util/query/navigation-query";
import useIsMobile from "@/components/useIsMobile";
import { Language } from "@/app/i18n/settings";
import { HeaderItemType, HeaderItemAttributes } from "@/util/query/header-query";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";
import { Lang } from "@/util/query/content-language-query";
import { normalizeHex, luminance, computeBorderColor } from "@/util/color-utils";
import SearchSvg from "@/assets/icons/search.svg?react";

// DYANMIC IMPORT
import dynamic from "next/dynamic";
import useIsTouchScreen from "./useIsTouchScreen";
// NavLogInClient now also accepts optional webNavigationOption for background colors
import { makeBaseComponentId as makeComponentId } from "@/util/make-component-id";
const NavLogInClient = dynamic(() => import("./nav-login-client").then((mod) => mod.NavLogInClient), {
    ssr: false,
    loading: () => <MenuIcon className="mx-[10px] w-[22px] h-[22px] justify-center" />,
});
const ButtonNotification = dynamic(() => import("./notification/button-notification"), { ssr: false });
const ButtonCart = dynamic(() => import("./button-cart"), { ssr: false });
const ShoppingNav = dynamic(() => import("@/components/business/navigation/templates/shopping.nav"), {
    ssr: false,
});
const BusinessNav = dynamic(() => import("@/components/business/navigation/templates/business.nav"), {
    ssr: false,
});

type LangOptions = {
    value: Language;
    label: string;
}[];

type NavMobileMenuLazyLoadProps = {
    shopcats: ShoppingCatagory[];
    login: boolean;
    level: CUSTOMER_LEVEL;
    headerItem: HeaderItemType[] | null;
    navShow?: boolean;
    contentLanguage: Lang[];
    webNavigationOption?: any | null; // raw JSON from WebNavigationComponent.webNavigationOption
    langOptionFromHeader?: any; // raw JSON from HeaderItemType.attributes
    favoriteEnable: boolean;
};

type Props = {
    webNavigation: WebNavigationComponent | null;
    template: string | null;
    fontProps: {
        fontSize: string | undefined;
        fontFamily: string | undefined;
    };
};

export const LanguageSelectionNav = ({ clasName, colorStyle, options, langList, isMobile }: { clasName?: string; colorStyle?: any; options: any[]; langList: any[]; isMobile: boolean }) => {
    const pathName = usePathname();
    const router = useRouter();
    const { lang } = useTranslation();
    const [selectedLang, setSelectedLang] = useState(lang);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const langOptions = options;

    useEffect(() => {
        setSelectedLang(lang);
    }, [lang]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOnValueChanged = (value: Language) => {
        setSelectedLang(value);
        setCookie(cookieName, value);
        setCookie(cookieIsChangeLang, "true");
        router.push(pathName);
        window.location.reload();
        setIsOpen(false);
    };

    const toggleDropdown = () => setIsOpen(!isOpen);

    return (
        <div id="header-cp-switchLanguage-item-content" className={cn("relative inline-block w-fit min-w-[100px]", clasName)} ref={dropdownRef}>
            <button
                id="header-cp-switchLanguage-item-button"
                onClick={toggleDropdown}
                className="bg-transparent border-none cursor-pointer focus:outline-none w-full text-left flex items-center justify-between gap-[10px]">
                {langList.map((option) => {
                    if ((option.loc).toLowerCase() === selectedLang) {
                        return <div key={option.id} id="header-cp-switchLanguage-item-text" className="font-normal lg:text-[18px] text-xl dynamic-color" 
                                    style={
                                        {
                                            "--dynamic-color": isMobile ? colorStyle?.menuTextNormal || "#3d3d3d" : colorStyle?.text || "#3d3d3d",
                                            "--dynamic-hover-color": isMobile ? colorStyle?.menuTextHover || "#254b7c" : colorStyle?.text || "#3d3d3d",
                                            "--dynamic-focus-color": isMobile ? colorStyle?.menuTextHover || "#254b7c" : colorStyle?.text || "#3d3d3d",
                                        } as React.CSSProperties
                                    }>{option.displayName}</div>;
                    }
                })}
                <span id="header-cp-switchLanguage-item-dropdown" className="relative top-1/2">
                    <ChevronDown 
                        id="header-cp-switchLanguage-item-dropdown-icon" 
                        className="h-4 w-4 opacity-90 dynamic-color" 
                        style={
                            {
                                "--dynamic-color": isMobile ? colorStyle?.menuTextNormal || "#3d3d3d" : colorStyle?.text || "#3d3d3d",
                                "--dynamic-hover-color": isMobile ? colorStyle?.menuTextHover || "#254b7c" : colorStyle?.text || "#3d3d3d",
                                "--dynamic-focus-color": isMobile ? colorStyle?.menuTextHover || "#254b7c" : colorStyle?.text || "#3d3d3d",
                            } as React.CSSProperties
                        }
                    />
                </span>
            </button>

            {isOpen && (
                <div id="header-cp-switchLanguage-item-dropdown-popup" className="absolute left-0 mt-1 min-w-[120px] w-fit bg-white border border-gray-300 rounded shadow-md z-10 p-1">
                    {langList.map((option, index) => (
                        <div id={`header-cp-switchLanguage-item-dropdown-popup-item-${index}`} className="cursor-pointer" key={index}>
                            <div
                                id="header-cp-switchLanguage-item-dropdown-popup-item-content"
                                key={option.id}
                                onClick={() => handleOnValueChanged((option.loc).toLowerCase())}
                                className={`px-2 h-8 flex items-center hover:bg-gray-200 rounded text-sm text-black ${
                                    selectedLang === (option.loc).toLowerCase() ? "bg-gray-100" : ""
                                }`}>
                                <span id="header-cp-switchLanguage-item-dropdown-popup-item-check" className="w-6 flex items-center justify-start">
                                    {selectedLang === (option.loc).toLowerCase() && <Check id="header-cp-switchLanguage-item-dropdown-popup-item-check-icon" className="h-4 w-4" />}
                                </span>
                                {option.displayName}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const NavSearch = ({ 
    clasName, 
    onEnter, 
    colorStyle, 
    customBgColor,
    customTextColor, 
    attributes,
    isMobile
}: { 
    clasName?: string; 
    onEnter?: () => void; 
    colorStyle: any; 
    customBgColor?: string; 
    customTextColor?: string; 
    attributes?: any;
    isMobile: boolean;
}) => {
    const searchparam = useSearchParams();
    const [searchInput, setSearchInput] = useState(searchparam.get("search") ?? "");
    const { t } = useTranslation();
    const rout = useRouter();

    const pathname = usePathname();
    const handleSearchInputChange = (e: any) => {
        setSearchInput(e.target.value);
    };

    const handleSearchEnter = (e: any) => {
        const kb = e as KeyboardEvent;
        if (kb.key.toLowerCase() === "enter") {
            e.preventDefault();
            const trimmed = searchInput.trim();
            if (trimmed !== "") {
                // Optionally update state to reflect trimmed value in input
                if (trimmed !== searchInput) setSearchInput(trimmed);
                rout.push("/search?search=" + encodeURIComponent(trimmed));
                onEnter?.();
            }
        }
    };

    useEffect(() => {
        setSearchInput(searchparam.get("search") ?? "");
    }, [pathname, searchparam]);

    const bg = (customBgColor || colorStyle?.headerBg || "#ffffff").trim();
    const text = (customTextColor || colorStyle?.text || "#3d3d3d").trim();


    const dynamicBorderColor = computeBorderColor(bg);
    return (
        <div 
            id="header-cp-search-item"
            className={cn("flex items-center max-w-[280px] border rounded-full px-4 gap-1 bg-transparent ", clasName)}
            style={{ borderColor: isMobile ? colorStyle?.menuTextNormal || "#3d3d3d" : colorStyle?.text || "#3d3d3d" }}
        >
            <Input
                className="w-full text-[18px] font-normal border-none h-full focus-visible:ring-0 focus-visible:ring-offset-0 dynamic-color dynamic-bg bg-transparent"
                type="text"
                id="searchInput"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchEnter}
                placeholder={(attributes && attributes.displayName) ? attributes.displayName : ""}
                style={
                    {
                        "--dynamic-color": isMobile ? colorStyle?.menuTextNormal || "#3d3d3d" : colorStyle?.text || "#3d3d3d",
                    } as React.CSSProperties
                }
                autoComplete="off"
            />
            <Link 
                id="header-cp-search-link"
                href={searchInput != "" ? "/search?search=" + searchInput : ""} aria-label="search"
            >
                <SearchSvg id="header-cp-search-icon" style={{ color: isMobile ? colorStyle?.menuTextNormal || "#3d3d3d" : colorStyle?.text || "#3d3d3d" }} width={18} height={18} />
            </Link>
        </div>
    );
};

export const ButtonNotificationLazyLoad = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        // Load after the page has hydrated
        setShow(true);
    }, []);

    return <>{show && <ButtonNotification />}</>;
};

export const ButtonCartLazyLoad = ({ attributes, priority, itemLength, isMobile }: { attributes: HeaderItemAttributes, priority: number, itemLength: number, isMobile: boolean }) => {
    const [show, setShow] = useState(false);

    const colorStyle = useContext(ColorStyleContext);

    useEffect(() => {
        // Load after the page has hydrated
        setShow(true);
    }, []);

    return <>{(show && attributes) && <ButtonCart colorStyle={colorStyle} attributes={attributes} priority={priority} itemLength={itemLength} isMobile={isMobile} />}</>;
};

const useIsScroll = () => {
    const [isMove, setIsMove] = useState<boolean>(false);

    useEffect(() => {
        const handleMove = () => {
            setIsMove(true);
        };

        document.addEventListener("scroll", handleMove, { passive: true });

        return () => {
            document.removeEventListener("scroll", handleMove);
        };
    }, []);

    return isMove;
};

export const NavMobileMenuLazyLoad = ({ shopcats, login, level, headerItem, webNavigationOption, contentLanguage, langOptionFromHeader, favoriteEnable }: NavMobileMenuLazyLoadProps) => {
    const [show, setShow] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const isMobile = useIsMobile();
    const isScroll = useIsScroll();
    const isClick = useRef(false);
    const { loc, t } = useTranslation();

    const handleClick = () => {
        isClick.current = true;
        setShow(true);
        setTrigger(!trigger);
    };

    useEffect(() => {
        if (isMobile && (isScroll || isClick.current)) {
            setShow(true);
        }
    }, [isMobile, isScroll]);

    return (
        <div id={makeComponentId("mobile-menu", `wrapper-${headerItem && headerItem.length}`)}>
            {show ? (
                <NavLogInClient
                    shopcats={shopcats}
                    login={login}
                    level={level}
                    trigger={trigger}
                    headerItem={headerItem}
                    webNavigationOption={webNavigationOption || null}
                    contentLanguage={contentLanguage}
                    loc={loc}
                    langOptionFromHeader={langOptionFromHeader}
                    favoriteEnable={favoriteEnable}
                />
            ) : (
                <MenuIcon
                    id={makeComponentId("mobile-menu", `trigger-${headerItem && headerItem.length}`)}
                    className="mx-[10px] w-[22px] h-[22px] justify-center"
                    onClick={handleClick}
                />
            )}
        </div>
    );
};

export const BusinessNavLazyLoad = ({ webNavigation, template, fontProps }: Props) => {
    const [show, setShow] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile === false) {
            setShow(true);
        }
    }, [isMobile]);

    return (
        <div id={makeComponentId("business-nav-wrapper", "nav-wrapper")}>
            {show && (
                <div
                    className="w-full flex gap-4 items-center"
                    id={makeComponentId("business-lazy", "lazy")}
                    style={
                        {
                            "--font-size": fontProps?.fontSize || "18px",
                            "--font-family": fontProps?.fontFamily || "kanit",
                        } as React.CSSProperties
                    }>
                    {template === "TEMPLATE_1" ? (
                        <ShoppingNav webNavigation={webNavigation} />
                    ) : template === "TEMPLATE_2" ? (
                        <BusinessNav webNavigation={webNavigation} />
                    ) : (
                        <BusinessNav webNavigation={webNavigation} />
                    )}
                </div>
            )}
        </div>
    );
};
